import {
  acceptFriendRequest,
  denyFriendRequest,
  ignoreFriendRequest,
  removeFriend,
} from '@/actions/friends';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge class names with Tailwind's clsx and twMerge utilities
 */
export function cn(...inputs: any[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Get membership type name from UGID
 */
export const getMembershipType = (UGID: number): string => {
  switch (UGID) {
    case 1:
      return 'Guest';
    case 2:
      return 'Exclusive Member';
    case 3:
      return 'Lifetime Member';
    case 4:
      return 'Local Host';
    case 5:
      return 'Administrator';
    default:
      return 'Unknown';
  }
};

/**
 * Get verification status name
 */
export const getVerification = (isVerified: number): string => {
  switch (isVerified) {
    case 1:
      return 'Real';
    case 2:
      return 'VIP';
    default:
      return 'None';
  }
};

interface FriendActionParams {
  userId: number;
  username: string;
  action: 'accept' | 'deny' | 'ignore' | 'remove' | 'cancel';
  router: any;
  setIsLoading: (loading: boolean) => void;
  shouldRefresh?: boolean;
  updateLocalState: (
    userId: number,
    action: 'accept' | 'deny' | 'ignore' | 'remove' | 'cancel'
  ) => void;
  toast: any;
  pathname: string;
  initialData: { pagination: { currentPage: number } };
  pageType: string;
  refreshCounts?: () => Promise<void>;
}

/**
 * Handle friend-related actions with proper error handling and UI updates
 */
export const handleFriendAction = async ({
  userId,
  username,
  action,
  router,
  setIsLoading,
  shouldRefresh,
  updateLocalState,
  toast,
  pathname,
  initialData,
  pageType,
  refreshCounts,
}: FriendActionParams): Promise<void> => {
  try {
    setIsLoading(true);

    let response;
    switch (action) {
      case 'accept':
        response = await acceptFriendRequest(userId);
        break;
      case 'deny':
        response = await denyFriendRequest(userId);
        break;
      case 'ignore':
        response = await ignoreFriendRequest(userId);
        break;
      case 'remove':
      case 'cancel':
        response = await removeFriend(userId);
        break;
      default:
        throw new Error('Invalid action');
    }

    if (!response.success) {
      throw new Error(response.error?.message || 'Something went wrong');
    }

    updateLocalState(userId, action);

    // Show success message based on action
    const messages = {
      accept: `Accepted friend request from ${username}`,
      deny: `Denied friend request from ${username}`,
      ignore: `Ignored friend request from ${username}`,
      remove: `Removed ${username} from friends`,
      cancel: `Cancelled friend request to ${username}`,
    };
    toast.success(messages[action]);

    // Only refresh the page if necessary (last item in paginated list)
    if (shouldRefresh) {
      const params = new URLSearchParams(window.location.search);
      params.set(
        `${pageType}Page`,
        (initialData.pagination.currentPage - 1).toString()
      );
      router.push(`${pathname}?${params.toString()}`);
    }

    // Refresh counts if provided
    if (refreshCounts) {
      await refreshCounts();
    }
  } catch (error: any) {
    console.error(`Error ${action} friend:`, error);
    toast.error(error.message || `Failed to ${action} friend`);
  } finally {
    setIsLoading(false);
  }
};

/**
 * Get timezone abbreviation from timezone identifier
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  // Map of timezone identifiers to consistent abbreviations
  const timezoneMap: Record<string, string> = {
    'America/Chicago': 'CST',
    'America/New_York': 'EST',
    'America/Los_Angeles': 'PST',
    'America/Denver': 'MST',
    'America/Phoenix': 'MST',
    'America/Anchorage': 'AKST',
    'America/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST', // China Standard Time
    'Australia/Sydney': 'AEST',
  };

  // If we have a direct mapping, use it
  if (timezoneMap[timezone]) {
    return timezoneMap[timezone];
  }

  // Otherwise fall back to the original implementation
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = { timeZoneName: 'short' };
  const timeString = date.toLocaleString('en-US', {
    ...options,
    timeZone: timezone,
  });

  // Extract the abbreviation but remove the 'D' for daylight if present
  const abbreviationMatch = timeString.match(/[A-Z]{3,4}$/);
  if (abbreviationMatch) {
    // Convert CDT to CST, EDT to EST, etc.
    const abbr = abbreviationMatch[0];
    return abbr.replace('DT', 'ST');
  }

  return timezone;
};

/**
 * Format a currency amount with the appropriate currency symbol
 * @param amount The amount to format
 * @param currency The currency code (e.g., 'USD', 'EUR')
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount?: number,
  currency: string = 'USD'
): string {
  if (amount === undefined) return 'N/A';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount / 100); // Assuming amount is in cents
}

/**
 * Format a date string into a human-readable format
 * @param dateString ISO date string
 * @returns Formatted date string
 */
export function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString));
}

/**
 * Truncate a string to a specified length and add ellipsis if needed
 * @param str String to truncate
 * @param length Maximum length
 * @returns Truncated string
 */
export function truncateString(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.substring(0, length)}...`;
}

/**
 * Convert a string to title case
 * @param str String to convert
 * @returns Title cased string
 */
export function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  );
}

/**
 * Create a URL-friendly slug from a string
 * @param str String to convert to slug
 * @returns URL-friendly slug
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
}
